import { defineStore } from 'pinia';
import { computed, ref, set, del } from 'vue';
import { isMatch } from 'lodash-es';
import type { MigrationTask, MigrationTaskProgressUpdate } from '@schema';
import {
  mapMigrationTask,
  type MappedMigrationTask
} from '@/common/store/pinia/migrationTasks/migrationTasksMapper';

export type MigrationTaskParentType = 'deployment' | 'replication';

interface StoredMigrationTask extends MigrationTask {
  parent_id: string;
  parent_type: MigrationTaskParentType;
  migration_id: string;
}

interface ProgressUpdatePayload {
  id?: string;
  task_id?: string;
  index?: number;
  message?: string;
  created_at?: string;
  progress?: number;
}

export const useMigrationTasksStore = defineStore('migrationTasks', () => {
  const tasksMap = ref<Record<string, StoredMigrationTask>>({});

  const getTaskById = (id: string): StoredMigrationTask | undefined => tasksMap.value[id];

  const setTasksForParent = (
    parent: { id?: string; migration_id?: string | null; tasks?: MigrationTask[] | null },
    parentType: MigrationTaskParentType,
    fallbackMigrationId = ''
  ) => {
    const parentId = parent.id;
    if (!parentId) return;

    const migrationId = parent.migration_id ?? fallbackMigrationId;
    const incoming = parent.tasks ?? [];

    const incomingIds = new Set(
      incoming
        .map(incomingTask => incomingTask.id)
        .filter((incomingTaskId): incomingTaskId is string => Boolean(incomingTaskId))
    );

    Object.entries(tasksMap.value).forEach(([taskId, storedTask]) => {
      if (storedTask.parent_id === parentId && !incomingIds.has(taskId)) {
        del(tasksMap.value, taskId);
      }
    });

    incoming.forEach((task) => {
      const { id } = task;
      if (!id) return;

      const existing = tasksMap.value[id];
      const next: StoredMigrationTask = {
        ...(existing ?? {}),
        ...task,
        parent_id: parentId,
        parent_type: parentType,
        migration_id: migrationId
      };
      if (existing && isMatch(existing, next)) return;
      set(tasksMap.value, id, next);
    });
  };

  const removeTasksForParent = (parentId: string) => {
    if (!parentId) return;

    Object.entries(tasksMap.value).forEach(([taskId, storedTask]) => {
      if (storedTask.parent_id === parentId) {
        del(tasksMap.value, taskId);
      }
    });
  };

  const mergeTask = (task: MigrationTask) => {
    const { id } = task;
    if (!id || !tasksMap.value[id] || isMatch(tasksMap.value[id], task)) return;

    const existing = tasksMap.value[id];

    set(tasksMap.value, id, { ...existing, ...task });
  };

  const appendProgressUpdate = (
    taskId: string,
    update: MigrationTaskProgressUpdate
  ) => {
    const existing = tasksMap.value[taskId];
    if (!existing) return;

    const updates = existing.progress_updates ?? [];

    const existingUpdateIndex = update.id
      ? updates.findIndex(existingUpdate => existingUpdate.id === update.id)
      : -1;

    if (existingUpdateIndex >= 0 && isMatch(updates[existingUpdateIndex], update)) return;

    const next = existingUpdateIndex >= 0
      ? updates.map((existingUpdate, updateIndex) => (
        updateIndex === existingUpdateIndex ? { ...existingUpdate, ...update } : existingUpdate
      ))
      : [...updates, update];

    set(tasksMap.value, taskId, {
      ...existing,
      progress_updates: next,
      updated_at: update.created_at ?? existing.updated_at
    });
  };

  const getMappedTasksByParent = computed(() => (parentId: string): MappedMigrationTask[] => {
    if (!parentId) return [];

    return Object.values(tasksMap.value)
      .filter(task => task.parent_id === parentId)
      .sort((firstTask, secondTask) => (firstTask.index ?? 0) - (secondTask.index ?? 0))
      .map(mapMigrationTask);
  });

  const onCompute = (data: SocketEvent<MigrationTask & ProgressUpdatePayload>) => {
    if (!data?.object) return;

    if (data.type === 'migration_tasks') {
      mergeTask(data.object as MigrationTask);
      return;
    }

    if (data.type === 'migration_task_progress_updates') {
      const progressUpdate = data.object as ProgressUpdatePayload;
      if (!progressUpdate.task_id) return;

      appendProgressUpdate(progressUpdate.task_id, {
        id: progressUpdate.id,
        index: progressUpdate.index,
        message: progressUpdate.message,
        created_at: progressUpdate.created_at,
        progress: progressUpdate.progress
      });
    }
  };

  return {
    tasksMap,
    getTaskById,
    setTasksForParent,
    removeTasksForParent,
    getMappedTasksByParent,
    onCompute
  };
}, {
  ws: {
    compute: 'onCompute'
  }
});

export default useMigrationTasksStore;
